import * as z from 'zod/mini';
import {
  ATTACHMENT_AUDIO_EXTENSIONS,
  ATTACHMENT_BINARY_EXTENSIONS,
  ATTACHMENT_CONTENT_TYPES,
  ATTACHMENT_FILENAME_MAX_LENGTH,
  ATTACHMENT_MAX_BYTES,
  ATTACHMENT_SIGNATURE_EXTENSIONS,
  ATTACHMENT_TEXT_EXTENSIONS,
  ATTACHMENT_TITLE_MAX_LENGTH,
  ATTACHMENT_VIDEO_EXTENSIONS,
  AUDIO_CONTENT_TYPES,
  AUDIO_MAX_BYTES,
  BYTES_PER_KIBIBYTE,
  BYTES_PER_MEBIBYTE,
  MEDIA_DURATION_MAX_SECONDS,
  SECONDS_PER_MINUTE,
  VIDEO_CONTENT_TYPES,
  VIDEO_MAX_BYTES,
} from '../constants.ts';
import { idSchema, NODE_TYPES, pathSchema, timestampSchema } from './common.ts';

// A file on a node: the switch's manual, a config export, the tester's PDF
// for a run, the plan, the invoice. Like a note it can hang off any of the
// six kinds of node and records who added it; like a photo its content type
// comes from the bytes, its bytes are served only with the session, and it
// is archived with a reason rather than deleted (deleting a node archives
// its files), so a manager alone removes an archived file for good.

export const ATTACHMENT_NODE_TYPES = NODE_TYPES;
export type AttachmentNodeType = (typeof ATTACHMENT_NODE_TYPES)[number];
export const attachmentNodeTypeSchema = z.enum(ATTACHMENT_NODE_TYPES);

/** What a file is for; the list drives the upload form's choice and the chip beside each file. */
export const ATTACHMENT_KINDS = [
  'manual',
  'config',
  'test',
  'plan',
  'invoice',
  // Memos (ROADMAP L5): a short video or a spoken note, which carry their own
  // size ceilings and play in the Files section rather than downloading.
  'video',
  'audio',
  'other',
] as const;
export type AttachmentKind = (typeof ATTACHMENT_KINDS)[number];
export const attachmentKindSchema = z.enum(ATTACHMENT_KINDS);

export const ATTACHMENT_KIND_LABELS: Readonly<Record<AttachmentKind, string>> = {
  manual: 'Manual',
  config: 'Config',
  test: 'Test result',
  plan: 'Plan',
  invoice: 'Invoice',
  video: 'Video memo',
  audio: 'Audio memo',
  other: 'Other',
};

/**
 * The kind a purchase invoice is filed under (ROADMAP J1). It was already in
 * the list above — a file on a device, like its manual — so nothing new is
 * stored for it; naming the value here is what lets the device page and the
 * report pick the invoices out of a device's files without repeating the
 * string in three places.
 */
export const INVOICE_KIND: AttachmentKind = 'invoice';

/** The two memo kinds, which carry their own ceilings and play where they are listed. */
export const MEDIA_KINDS = ['video', 'audio'] as const;
export type MediaKind = (typeof MEDIA_KINDS)[number];

export const videoContentTypeSchema = z.enum(VIDEO_CONTENT_TYPES);
export const audioContentTypeSchema = z.enum(AUDIO_CONTENT_TYPES);
export type MediaContentType =
  z.infer<typeof videoContentTypeSchema> | z.infer<typeof audioContentTypeSchema>;

/** Whether a kind is a memo, so it is capped, played, and labelled as one. */
export function isMediaKind(kind: AttachmentKind): kind is MediaKind {
  return kind === 'video' || kind === 'audio';
}

/**
 * The ceiling one upload of this kind may reach. A memo is bigger than a
 * manual and a video is bigger than a spoken note, so each kind says its own
 * rather than every file sharing the smallest limit that fits them all.
 */
export function attachmentMaxBytes(kind: AttachmentKind): number {
  if (kind === 'video') {
    return VIDEO_MAX_BYTES;
  }
  return kind === 'audio' ? AUDIO_MAX_BYTES : ATTACHMENT_MAX_BYTES;
}

/** The largest upload any kind may reach, for the check made before a body is read. */
export const ATTACHMENT_ANY_MAX_BYTES = Math.max(
  ATTACHMENT_MAX_BYTES,
  VIDEO_MAX_BYTES,
  AUDIO_MAX_BYTES,
);

/**
 * The memo kind a set of bytes turned out to be, or null when they are not a
 * memo at all. What the person chose on the form is checked against this, so a
 * video filed as an audio memo — or a PDF filed as either — is refused rather
 * than stored under a kind its bytes do not support.
 */
export function mediaKindOf(contentType: AttachmentContentType): MediaKind | null {
  if (z.safeParse(videoContentTypeSchema, contentType).success) {
    return 'video';
  }
  return z.safeParse(audioContentTypeSchema, contentType).success ? 'audio' : null;
}

export const attachmentContentTypeSchema = z.enum(ATTACHMENT_CONTENT_TYPES);
export type AttachmentContentType = z.infer<typeof attachmentContentTypeSchema>;

export const attachmentTitleSchema = z
  .string()
  .check(z.trim(), z.maxLength(ATTACHMENT_TITLE_MAX_LENGTH));

export const attachmentFilenameSchema = z
  .string()
  .check(z.trim(), z.minLength(1), z.maxLength(ATTACHMENT_FILENAME_MAX_LENGTH));

export const attachmentSchema = z.object({
  id: idSchema,
  nodeType: attachmentNodeTypeSchema,
  nodeId: idSchema,
  kind: attachmentKindSchema,
  /** '' when the file goes by its name alone. */
  title: attachmentTitleSchema,
  /** The name it was uploaded with, cleaned of any path. */
  filename: attachmentFilenameSchema,
  /** What the Worker found the bytes to be, never what the browser said. */
  contentType: attachmentContentTypeSchema,
  sizeBytes: z.int().check(z.positive()),
  /**
   * How long a memo runs, in whole seconds (ROADMAP L5), read off the file by
   * the browser before it uploaded it. Null for a file that is not a memo, and
   * for a memo whose duration the browser could not read — the list says
   * "length unknown" rather than claiming one.
   */
  durationSeconds: z.nullable(z.int().check(z.gte(0), z.lte(MEDIA_DURATION_MAX_SECONDS))),
  /**
   * The plan revision this file replaces (ROADMAP D9), or null for a first
   * upload. A file nothing points at is the head of its chain: the current
   * plan, and the only one a node's file list shows. Defaulted, so a payload
   * cached before D9 reads as a file that supersedes nothing.
   */
  supersedesAttachmentId: z._default(z.nullable(idSchema), null),
  /** Null once the uploader's account is removed. */
  userId: z.nullable(idSchema),
  username: z.string(),
  /** The uploader's display name at the time; '' when they had none. */
  displayName: z.string(),
  createdAt: timestampSchema,
  /** When the file was archived; null while it is current. */
  archivedAt: z.nullable(timestampSchema),
  archivedBy: z.nullable(idSchema),
  archivedByUsername: z.string(),
  archiveReason: z.string(),
  /** The node's name and path from the client down, as they were when archived; empty while current. */
  nodeName: z.string(),
  path: pathSchema,
});
export type Attachment = z.infer<typeof attachmentSchema>;

/** PATCH /api/attachments/:id: the title, the kind, or both. */
export const attachmentPatchSchema = z.partial(
  z.strictObject({ title: attachmentTitleSchema, kind: attachmentKindSchema }),
);
export type AttachmentPatch = z.infer<typeof attachmentPatchSchema>;

export const attachmentListSchema = z.object({ attachments: z.array(attachmentSchema) });
/** GET /api/attachments/:id/revisions: what this plan replaced, newest first. */
export const attachmentRevisionsSchema = z.object({ revisions: z.array(attachmentSchema) });
export const attachmentEnvelopeSchema = z.object({ attachment: attachmentSchema });

/**
 * Multipart field names of an upload: the file, its kind, an optional
 * title, and the id the client already gave it, so a queued upload replayed
 * twice files one file rather than two.
 */
export const ATTACHMENT_FORM_FIELDS = {
  file: 'file',
  kind: 'kind',
  title: 'title',
  /** A memo's run time in whole seconds, read by the browser; empty for unknown. */
  duration: 'duration',
  id: 'id',
} as const;

/**
 * The run time part of an upload: whole seconds, or empty for "the browser
 * could not read one". Anything else is a client that got it wrong and is
 * refused rather than stored as a length nobody measured.
 */
export const durationFieldSchema = z.nullable(
  z.int().check(z.gte(0), z.lte(MEDIA_DURATION_MAX_SECONDS)),
);

/** "0:42", "12:05", "1:04:07": a memo's run time, or '' when none was read. */
export function formatDuration(seconds: number | null): string {
  if (seconds === null) {
    return '';
  }
  const minutes = Math.floor(seconds / SECONDS_PER_MINUTE);
  const rest = String(seconds % SECONDS_PER_MINUTE).padStart(2, '0');
  if (minutes < SECONDS_PER_MINUTE) {
    return `${String(minutes)}:${rest}`;
  }
  const hours = Math.floor(minutes / SECONDS_PER_MINUTE);
  return `${String(hours)}:${String(minutes % SECONDS_PER_MINUTE).padStart(2, '0')}:${rest}`;
}

/** URL path of a file's bytes; requires the session cookie. */
export function attachmentFileHref(id: string): string {
  return `/api/attachments/${id}/file`;
}

/** What a file is called where one line names it: its title, else its file name. */
export function attachmentLabel(attachment: Pick<Attachment, 'title' | 'filename'>): string {
  return attachment.title === '' ? attachment.filename : attachment.title;
}

/**
 * Content types a browser shows or plays where it is given them; everything
 * else is offered as a download. A memo is served inline so the Files section
 * can play it in place (ROADMAP L5) rather than downloading a clip to hear a
 * sentence; `nosniff` and the type the bytes actually are keep that safe.
 */
export function isViewableInline(contentType: AttachmentContentType): boolean {
  return (
    contentType === 'application/pdf' ||
    contentType.startsWith('image/') ||
    mediaKindOf(contentType) !== null
  );
}

/** What the file picker offers: every accepted extension, as an `accept` attribute. */
export const ATTACHMENT_ACCEPT = [
  ...ATTACHMENT_SIGNATURE_EXTENSIONS,
  ...ATTACHMENT_TEXT_EXTENSIONS,
  ...ATTACHMENT_BINARY_EXTENSIONS,
  ...ATTACHMENT_VIDEO_EXTENSIONS,
  ...ATTACHMENT_AUDIO_EXTENSIONS,
]
  .map((extension) => `.${extension}`)
  .join(',');

/** "812 B", "24 KB", "1.5 MB": a size as a list shows it. */
export function formatFileSize(bytes: number): string {
  if (bytes < BYTES_PER_KIBIBYTE) {
    return `${String(bytes)} B`;
  }
  if (bytes < BYTES_PER_MEBIBYTE) {
    return `${String(Math.round(bytes / BYTES_PER_KIBIBYTE))} KB`;
  }
  return `${(bytes / BYTES_PER_MEBIBYTE).toFixed(1)} MB`;
}
